/* eslint-disable import/order */
import * as Ably from 'ably';
import qs from 'qs';

// @ts-expect-error ts-migrate(2307) FIXME: Cannot find module 'Hub/Receipt' or its correspond... Remove this comment to see the full error message
import { setHubPrintLoadingCompleted, setHubPrintLoadingError } from 'Hub/Receipt';
import store from 'pages/store';
import {
  updateConnectionStatus,
  updateHubConnectionStatus,
  updateBrowserCFDConnectionStatus,
  updateDevices,
  clearDevices,
} from 'pages/store/reducers/hub';
import { getCFDAppUrl, getBrandingConfig } from 'pages/views/devices/utils';
import { registerHubSmartEvents, removeHubSmartEvents } from 'pages/views/devices/utils/client';
import Logger from 'pages/views/devices/utils/logger';
import { OPTIONS, getOptions } from 'pages/views/devices/utils/options';
import { getCFDEnabled } from 'pages/views/devices/utils/sale-handler';
import {
  getHubEnabled,
  getChannelName,
  getHubConnectedOnce,
  setHubConnectedOnce,
  getHubSmartState,
  removeHubEnabled,
  safeGetItem,
  safeSetItem,
} from 'pages/views/devices/utils/storage';
import { Eventbus } from 'root/apps/app/src/data/Eventbus';

import {
  HUB_MESSAGES,
  CFD_MESSAGES,
  PRINTER_MESSAGES,
  STATUS,
  PRINTER_KINDS,
  PRINTER_STATUSES,
  MEMBER_TYPE,
  PrintData,
  isHubMessage,
  isPrinterMessage,
  isCfdMessage,
  Devices,
} from './types';
import moment from 'moment';
import { sleep } from 'root/apps/label/utils/async';

const EventToLoggerKeyMap = {
  HUB_MESSAGES: 'Hub',
  CFD_MESSAGES: 'CFD',
  PRINTER_MESSAGES: 'Printers',
};

export const getLoggerKeyFromEvent = (event: string) => {
  let loggerKey = '';
  if (Object.values(HUB_MESSAGES).includes(event as HUB_MESSAGES)) loggerKey = 'HUB_MESSAGES';
  else if (Object.values(CFD_MESSAGES).includes(event as CFD_MESSAGES)) loggerKey = 'CFD_MESSAGES';
  else if (Object.values(PRINTER_MESSAGES).includes(event as PRINTER_MESSAGES))
    loggerKey = 'PRINTER_MESSAGES';

  return EventToLoggerKeyMap[loggerKey as keyof typeof EventToLoggerKeyMap];
};

type Event<Type> = { event: Type; data: any };

export class AblyClient {
  CFD_MESSAGES = CFD_MESSAGES;

  PRINTER_KINDS = PRINTER_KINDS;

  PRINTER_STATUSES = PRINTER_STATUSES;

  PRINTER_MESSAGES = PRINTER_MESSAGES;

  status: STATUS;

  protected channelName: string;

  private isUnLoading = false;

  private messageQueue: {
    hub: Event<HUB_MESSAGES>[];
    cfd: Event<CFD_MESSAGES>[];
    printer: Event<PRINTER_MESSAGES>[];
  } = { hub: [], cfd: [], printer: [] };

  private isSaleHidden: boolean;

  private client: Ably.Realtime | undefined;

  private channel: Ably.Types.RealtimeChannelCallbacks | undefined;

  constructor() {
    this.status = STATUS.DISCONNECTED;
    this.isSaleHidden = true;
    this.channelName = getChannelName();

    window.addEventListener('load', () => {
      const updateOnlineStatus = () => {
        if (navigator.onLine) {
          this.connect();
        } else if (!navigator.onLine) {
          this.disconnect();
        }
      };

      window.addEventListener('online', updateOnlineStatus);
      window.addEventListener('offline', updateOnlineStatus);
    });
  }

  public async init(channelName: string) {
    this.channelName = channelName;

    await this.connect();
  }

  public getCFDParams = () => {
    return qs.stringify(
      {
        channelName: this.channelName,
        accountId: window.merchantos.session.getSystemCustomerID(),
        locale: getOptions()[OPTIONS.LOCALE],
      },
      { addQueryPrefix: true }
    );
  };

  public getAppUrl = () => {
    return `${getCFDAppUrl()}${this.getCFDParams()}`;
  };

  public send(event: PRINTER_MESSAGES.PRINT, data: PrintData): void;
  public send(event: CFD_MESSAGES, data?: any): void;
  public send(event: HUB_MESSAGES, data?: any): void;
  public send(event: CFD_MESSAGES | HUB_MESSAGES | PRINTER_MESSAGES, data: any = {}) {
    if (this.status === STATUS.CONNECTED) {
      if (event === CFD_MESSAGES.HIDE_SALE && this.isSaleHidden) return;

      this.isSaleHidden = [CFD_MESSAGES.HIDE_SALE, CFD_MESSAGES.COMPLETE_SALE].includes(
        event as CFD_MESSAGES
      );

      if (
        event === HUB_MESSAGES.UPDATE_CONFIG &&
        typeof data?.config?.[OPTIONS.IS_DEBUG_MODE] === 'boolean'
      ) {
        // @ts-expect-error ts-migrate(2339) FIXME: Property 'setLog' does not exist on type 'Rest'.
        Ably.Rest.prototype.setLog({ level: this.getLogLevel() });
      }

      if (CFD_MESSAGES[event as keyof typeof CFD_MESSAGES] && !getCFDEnabled()) return;

      this.channel?.publish(event, data);

      Logger.debug(`${getLoggerKeyFromEvent(event)}: ${event}`, data);
    } else if (event !== CFD_MESSAGES.HIDE_SALE) {
      if (isHubMessage(event)) {
        this.messageQueue.hub.push({ event, data });
      } else if (isPrinterMessage(event)) {
        this.messageQueue.printer.push({ event, data });
      } else if (isCfdMessage(event)) {
        this.messageQueue.cfd.push({ event, data });
      }

      Logger.debug(`Hub: Connection not yet established. Enqueuing ${event}`, data);
    }
  }

  private getLogLevel() {
    return getOptions()[OPTIONS.IS_DEBUG_MODE] ? 2 : 0;
  }

  public connect() {
    if (
      document.visibilityState !== 'visible' ||
      !getHubEnabled() ||
      ['connected', 'connecting'].includes(this.client?.connection?.state as string)
    ) {
      return;
    }

    Logger.debug(`Hub: channelName ${this.channelName}`);
    if (this.client) {
      this.client.connection.connect();
    } else {
      this.client = new Ably.Realtime({
        clientId: MEMBER_TYPE.RETAIL,
        authUrl: `${getCFDAppUrl('hub-backend')}/api/socket/auth`,
        log: {
          level: this.getLogLevel(),
          handler: Logger.debug,
        },
      });

      this.client.connection.once('connected', () => {
        this.channel = this.client!.channels.get(`private:${this.channelName}`);
        this.channel.on('failed', this.onChannelFailed);
        this.channel.subscribe(HUB_MESSAGES.GET_CONFIG, this.onGetConfig);
        this.channel.subscribe(HUB_MESSAGES.DEVICES, this.onDevices);
        this.channel.presence.subscribe('enter', this.onPresenceMemberAdded);
        this.channel.presence.subscribe('leave', this.onPresenceMemberLeave);
        this.channel.subscribe(PRINTER_MESSAGES.PRINT_COMPLETED, this.onPrintCompleted);
        this.channel.subscribe(PRINTER_MESSAGES.PRINT_ERROR, this.onPrintError);
        this.channel.subscribe(CFD_MESSAGES.TIP_CONFIRMED, this.onTipSelected);
      });

      this.client.connection.on('connected', () => {
        this.setAblyStatus(STATUS.CONNECTED);
        this.checkOnLastConnection();
        this.channel!.presence.get(this.onPresenceMemberGet);
        this.channel!.presence.enter();
      });

      this.client.connection.on('closed', () => this.setAblyStatus(STATUS.DISCONNECTED));
      this.client.connection.on('failed', () => this.setAblyStatus(STATUS.ERROR));
      this.client.connection.on('suspended', () => this.setAblyStatus(STATUS.ERROR));
      this.client.connection.on('disconnected', () => this.setAblyStatus(STATUS.DISCONNECTED));
    }

    window.addEventListener('beforeunload', this.setUnloading);
    setTimeout(removeHubSmartEvents, 1000);
  }

  private isClientConnected = (member: string) =>
    new Promise(resolve => {
      this.channel?.presence.get((_err, members) => {
        const isConnected = members?.some(({ clientId }) => clientId === member);
        return resolve(isConnected);
      });
    });

  private onDevices: Ably.Types.messageCallback<Ably.Types.Message> = async message => {
    const { defaultPrinters, selectedDisplay, version } = message.data as Devices;

    if (defaultPrinters?.LABEL || defaultPrinters?.RECEIPT) {
      this.sendMessages('printer');
    }

    Logger.debug(`Hub: ${HUB_MESSAGES.DEVICES}`, message.data);
    const isBrowserCFDConnected = await this.isClientConnected(MEMBER_TYPE.CFD);

    // version is sent on initial connect only
    const payload = version ? { version } : {};

    // Apply both printers and CFD coming from hub if BrowserCFD isn't connected
    if (!isBrowserCFDConnected) {
      Object.assign(payload, { defaultPrinters, selectedDisplay });
    }

    // Only apply printers if BrowserCFD is connected
    if (message.clientId === MEMBER_TYPE.HUB && isBrowserCFDConnected) {
      Object.assign(payload, { defaultPrinters });
    }

    // Only use display if Browser CFD is connected. Skip printers
    if (message.clientId === MEMBER_TYPE.CFD) {
      Object.assign(payload, { selectedDisplay });
    }

    store.dispatch(updateDevices(payload));
  };

  private onPresenceMemberAdded: Ably.Types.messageCallback<Ably.Types.PresenceMessage> = ({
    clientId,
    connectionId,
  }) => {
    if (this.client?.connection.id !== connectionId && clientId === MEMBER_TYPE.RETAIL) {
      this.disconnect();
    } else if (clientId === MEMBER_TYPE.HUB) {
      this.setPreviousSuccessfulConnection();
      store.dispatch(updateHubConnectionStatus(STATUS.CONNECTED));
      if (!getHubConnectedOnce()) {
        setHubConnectedOnce();
      }
      this.sendMessages('hub');
    } else if (clientId === MEMBER_TYPE.CFD) {
      this.setPreviousSuccessfulConnection();
      store.dispatch(updateBrowserCFDConnectionStatus(STATUS.CONNECTED));
      this.sendMessages('cfd');
    }
  };

  private onPresenceMemberLeave = () => {
    this.channel?.presence.get(this.onPresenceMemberGet);
  };

  private onPresenceMemberGet: Ably.Types.realtimePresenceGetCallback = (err, members) => {
    if (err) {
      Logger.error(err);
      return;
    }
    const isHubConnected = members.some(({ clientId }) => clientId === MEMBER_TYPE.HUB);
    const isCFDConnected = members.some(({ clientId }) => clientId === MEMBER_TYPE.CFD);

    if (isHubConnected || isCFDConnected) {
      this.setPreviousSuccessfulConnection();
    }

    store.dispatch(
      updateHubConnectionStatus(isHubConnected ? STATUS.CONNECTED : STATUS.DISCONNECTED)
    );
    store.dispatch(
      updateBrowserCFDConnectionStatus(isCFDConnected ? STATUS.CONNECTED : STATUS.DISCONNECTED)
    );
    if (isHubConnected) {
      this.sendMessages('hub');
    }
    if (isCFDConnected) {
      this.sendMessages('cfd');
    }
  };

  private checkOnLastConnection = async () => {
    // Check if there was a successful connection AFTER connecting. Maybe the merchant was on holiday for a week.
    await sleep(5000);
    if (moment().diff(moment(this.getPreviousConnection()), 'days') > 7) {
      this.disconnect();
      removeHubEnabled();
      removeHubSmartEvents();
    }
  };

  private getPreviousConnection = () =>
    safeGetItem(
      'HUB_PREVIOUS_SUCCESSFUL_CONNECTION_TO_CLIENT',
      moment()
        .subtract(6, 'days')
        .format()
    )!; // Allow retail to be alone in the channel for one day for new users to give time to new users to install the hub

  private setPreviousSuccessfulConnection() {
    safeSetItem('HUB_PREVIOUS_SUCCESSFUL_CONNECTION_TO_CLIENT', moment().format());
  }

  private sendMessages = (type: 'hub' | 'cfd' | 'printer') => {
    let message = this.messageQueue[type].pop();
    while (message) {
      this.send(message.event as any, message.data);
      message = this.messageQueue[type].pop();
    }
  };

  private setAblyStatus(status: STATUS) {
    this.status = status;

    if (!this.isUnLoading && store.getState().hub.connection.ably !== status) {
      store.dispatch(updateConnectionStatus(status));
    }
  }

  public disconnect() {
    if (![STATUS.CONNECTED, STATUS.READY].includes(this.status)) return;

    store.dispatch(clearDevices());
    this.send(HUB_MESSAGES.DISCONNECT);
    window.removeEventListener('beforeunload', this.setUnloading);

    // Make sure the disconnect event gets sent before the members gets updated
    setTimeout(() => {
      this.client?.close();
      this.channel?.presence.leave();
      this.setAblyStatus(STATUS.DISCONNECTED);
    }, 250);
  }

  public getHubPrinterStatus(kind: PRINTER_KINDS): PRINTER_STATUSES {
    const { connection, defaultPrinters } = store.getState().hub;

    return (getHubSmartState()?.defaultPrinters[kind]?.status ||
      (connection.hub === STATUS.CONNECTED && defaultPrinters?.[kind]?.status)) as PRINTER_STATUSES;
  }

  private onChannelFailed = () => {
    this.setAblyStatus(STATUS.ERROR);
  };

  private onGetConfig = async () => {
    this.send(HUB_MESSAGES.UPDATE_CONFIG, {
      config: {
        ...getOptions(),
        ...(await getBrandingConfig()),
      },
    });
  };

  private onPrintCompleted = (message: TSFixMe) => {
    setHubPrintLoadingCompleted();
    Logger.debug('Printers: onPrintCompleted', message);
  };

  private onPrintError = (message: TSFixMe) => {
    const kind = message.data.includes('label') ? PRINTER_KINDS.LABEL : PRINTER_KINDS.RECEIPT;

    setHubPrintLoadingError({ error: message.data, kind });
    Logger.debug('Printers: onPrintError', message);
  };

  private setUnloading = () => {
    this.isUnLoading = true;
    registerHubSmartEvents();
  };

  onTipSelected = (message: Ably.Types.Message) => {
    const { data } = message;
    Eventbus.emit(CFD_MESSAGES.TIP_CONFIRMED, { data });
  };
}
